import {
  Autocomplete,
  Checkbox,
  TextField,
  Divider,
  Stack,
  Grid,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from '@material-ui/core'
import { useState, useEffect } from 'react'
import commonService from 'src/components/chatbot/services/commonService'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'

function PanelsFilter({ selectedPanels = [], setSelectedPanels }) {
  const [input, setInput] = useState('')
  const [panelOptions, setPanelOptions] = useState([])
  const [filteredOptions, setFilteredOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [hoveredOption, setHoveredOption] = useState(null)
  const [open, setOpen] = useState(false)

  const handleFetchPanels = async () => {
    setLoading(true)
    try {
      const results = await commonService.fetchPanels()
      setPanelOptions(results)
      setFilteredOptions(results)
    } catch (error) {
      console.error('Error fetching panels:', error)
      setPanelOptions([])
      setFilteredOptions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    handleFetchPanels()
  }, [])

  useEffect(() => {
    if (!input.trim()) {
      setFilteredOptions(panelOptions)
    } else {
      const filtered = panelOptions.filter(
        (panel) =>
          (panel.name && panel.name.toLowerCase().includes(input.toLowerCase())) ||
          (panel.id && panel.id.toLowerCase().includes(input.toLowerCase())),
      )
      setFilteredOptions(filtered)
    }
  }, [input, panelOptions])

  const getOptionLabel = (option) => {
    if (!option) return ''
    if (typeof option === 'string') return option

    return option.label || `${option.name} (${option.id})`
  }

  const handleClearInput = () => {
    setInput('')
    setFilteredOptions(panelOptions)
  }

  return (
    <Stack direction="column" spacing={2} divider={<Divider />}>
      {selectedPanels !== null && setSelectedPanels && (
        <>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Selected Panels ({selectedPanels.length})
              </Typography>
              {selectedPanels.length > 0 && (
                <IconButton size="small" onClick={() => setSelectedPanels([])} title="Clear all panels">
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                maxHeight: '200px',
                overflowY: 'auto',
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#555',
                  },
                },
              }}
            >
              {selectedPanels.length > 0 ? (
                selectedPanels.map((panel) => {
                  // Only show the name, not the id in parentheses
                  const label = panel.name || panel.label || ''
                  const truncatedLabel = label.length > 32 ? `${label.slice(0, 29)}...` : label
                  const needsTooltip = label.length > 32

                  return (
                    <Tooltip key={panel.value || panel.id} title={needsTooltip ? label : ''}>
                      <Chip
                        label={truncatedLabel}
                        onDelete={() => {
                          const newPanels = selectedPanels.filter(
                            (p) => (p.value || p.id) !== (panel.value || panel.id),
                          )
                          setSelectedPanels(newPanels)
                        }}
                        sx={{
                          maxWidth: '300px',
                          '& .MuiChip-label': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                          '&:hover': {
                            backgroundColor: 'primary.light',
                          },
                          transition: 'background-color 0.2s',
                        }}
                      />
                    </Tooltip>
                  )
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No panels selected
                </Typography>
              )}
            </Box>
          </Box>
          <Autocomplete
            multiple
            options={filteredOptions}
            loading={loading}
            disableCloseOnSelect={false}
            disableClearable
            filterOptions={(x) => x}
            inputValue={input}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'reset' || reason === 'blur') return
              setInput(newInputValue)
            }}
            clearOnBlur={false}
            blurOnSelect
            renderInput={(params) => {
              const { InputProps, ...rest } = params
              return (
                <Grid sx={{ display: 'flex' }}>
                  <TextField
                    {...rest}
                    label="Search panels by name or ID"
                    variant="outlined"
                    placeholder="Type to search panels..."
                    InputProps={{
                      ...InputProps,
                      startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                      endAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {InputProps.endAdornment}
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {input.length > 0 && (
                            <IconButton
                              size="small"
                              aria-label="Clear search input"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleClearInput()
                              }}
                              tabIndex={0}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>
              )
            }}
            renderOption={(props, option, { selected }) => (
              <li
                {...props}
                onMouseEnter={() => setHoveredOption(option)}
                onMouseLeave={() => setHoveredOption(null)}
                style={{
                  transition: 'background-color 0.2s',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    backgroundColor: hoveredOption === option ? 'action.hover' : 'transparent',
                    transition: 'background-color 0.2s',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={Array.isArray(selectedPanels) && selectedPanels.some((p) => p?.value === option?.id)}
                      sx={{
                        '&:hover': { color: 'primary.main' },
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: selectedPanels.some((p) => p?.value === option?.id) ? 500 : 400,
                          color: selectedPanels.some((p) => p?.value === option?.id) ? 'primary.main' : 'inherit',
                        }}
                      >
                        {option?.name}
                      </Typography>
                      {option?.id && <Chip label={option.id} size="small" sx={{ height: 20, mt: 0.5 }} />}
                    </Box>
                  </Box>
                </Box>
              </li>
            )}
            getOptionLabel={getOptionLabel}
            value={selectedPanels || []}
            onChange={(_, val) => {
              const formattedVal = val.map((panel) => {
                if (panel.value) return panel // Already formatted
                return {
                  value: panel.id,
                  label: `${panel.name} (${panel.id})`,
                  name: panel.name,
                  id: panel.id,
                }
              })
              // Enforce single selection: keep only the last selected item
              const last = formattedVal.length > 0 ? formattedVal[formattedVal.length - 1] : null
              setSelectedPanels(last ? [last] : [])
              setOpen(false)
            }}
            isOptionEqualToValue={(option, value) => {
              return option?.id === value?.value || option?.id === value?.id
            }}
            noOptionsText={
              <Box sx={{ py: 2, textAlign: 'center' }}>
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography color="text.secondary">
                    {panelOptions.length === 0 ? 'No panels available' : 'No results found'}
                  </Typography>
                )}
              </Box>
            }
            ListboxProps={{
              style: {
                maxHeight: '400px',
              },
            }}
          />
        </>
      )}
    </Stack>
  )
}

export default PanelsFilter
